import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function TransportationTable12({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =============================
     SORT DATA (Highest → Lowest)
  ============================== */

  const sortedData = [...data]
    .map((d) => ({
      ...d,
      emp_count: Number(d.emp_count || 0),
    }))
    .sort((a, b) => b.emp_count - a.emp_count);

  const ageGroups = sortedData.map((d) => d.age_group);
  const counts = sortedData.map((d) => d.emp_count);

  const totalEmployment = counts.reduce((sum, val) => sum + val, 0);

  const topAgeGroup = sortedData[0];

  /* =============================
     CHART DATA
  ============================== */

  const chartData = {
    labels: ageGroups,
    datasets: [
      {
        label: "Employment Count",
        data: counts,
        backgroundColor: ageGroups.map(
          (_, idx) => `hsl(${(idx * 45) % 360}, 70%, 55%)`,
        ),
        borderRadius: 6,
        barThickness: 25,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "📊 Employment Snapshot by Age Group",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            const percent = ((value / totalEmployment) * 100).toFixed(1);
            return value.toLocaleString("en-US") + ` (${percent}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Age Group",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
        },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
    },
  };

  /* =============================
     PDF EXPORT
  ============================== */

   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text(APP_VERSION, 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );

       pdf.save("age_group_snapshot.pdf");
     });
   };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Age Group
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalEmployment.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Top Age Group</p>
            <p className="text-lg font-bold text-green-800">
              {topAgeGroup.age_group}
            </p>
          </div>

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Top Group Share</p>
            <p className="text-xl font-bold text-purple-800">
              {((topAgeGroup.emp_count / totalEmployment) * 100).toFixed(1)}%
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Employment</th>
                  <th className="p-2">Share %</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${
                      idx === 0
                        ? "bg-green-100 font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.age_group}</td>
                    <td className="p-2">
                      {row.emp_count.toLocaleString("en-US")}
                    </td>
                    <td className="p-2">
                      {((row.emp_count / totalEmployment) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="w-full h-[420px] bg-white rounded-lg shadow-md p-4">
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
